import { parseToHtml } from "@/utility/parseToHtml";

/**
 * Phone Number Validation Patterns
 *
 * Valid (no error):
 * - tel:+12345678910 — exactly tel:+ followed by 11 digits
 *
 * Shows error/warning — detected by looksLikeTelHref but fails isValidTelHref:
 *
 * tel:+1234567890            — 10 digits (need 11)
 * tel:+123456789101          — 12 digits (need 11)
 * tel:12345678910            — Missing +
 * tel:121231321312231        — Missing +, too many digits
 * tel:+1-234-567-8910        — Contains dashes
 * tel:+1 234 567 8910        — Contains spaces
 * tel:abc                    — Letters instead of number
 * tel:                       — Empty number
 * tele:+12345678910          — Misspelled prefix
 * telephone:+12345678910     — Misspelled prefix
 * tl:+12345678910            — Misspelled prefix
 * tel+12345678910            — Missing colon
 * tele+12345678910           — Missing colon
 * tel;+12345678910           — Wrong separator (semicolon)
 * tel.+12345678910           — Wrong separator (dot)
 * tel-+12345678910           — Wrong separator (dash)
 * tel://+12345678910         — Double slash like URL scheme
 * tel#:+12345678910           — Hash in prefix
 * tel#$%:+12345678910        — Multiple symbols in prefix
 * tel1+12345678910           — Digit instead of separator (tel1+)
 * tel1:+12345678910          — Extra digit before colon
 * tel:1+12345678910          — Colon present but 1+ instead of +1
 * tel12345678910             — Missing colon and +, digits right after prefix
 * tel2345678910              — Same, partial number
 * tele12345678910            — Misspelled prefix, no separators
 * telephone12345678910       — Misspelled prefix, no separators
 * ph:+12345678910            — Non-tel prefix
 * ph:12345678910             — Non-tel prefix, no +
 * call:+12345678910          — Non-tel prefix
 * call:12345678910           — Non-tel prefix, no +
 * phone:12345678910          — Non-tel prefix, no +
 * +12345678910               — Bare phone number with + (no prefix)
 * 12345678910                — Bare digits (7-15 digits)
 * 1-800-555-0123             — Formatted number with dashes
 * (234)567-8910              — Formatted number with parens
 *
 * NOT detected (ignored, no error):
 *
 * https://example.com        — Known non-phone scheme
 * mailto:user@test.com       — Known non-phone scheme
 * http://example.com?tel=123 — Known non-phone scheme
 * javascript:void(0)         — Known non-phone scheme
 * #section                   — Fragment link
 * //cdn.example.com          — Protocol-relative URL
 * /page?type=tel             — Starts with /
 * example.com                — No matching prefix pattern
 * test.com                   — Starts with "t" but no digits after separator
 * twitter.com                — Same, letters after dot not digits
 * 12345                      — Too few digits (< 7)
 */

export function looksLikeTelHref(href) {
  if (typeof href !== "string") return false;
  const cleaned = normalizeTelHref(href);
  if (!cleaned) return false;
  const base = cleaned.split("?")[0];
  if (!base) return false;

  // Exact "tel:" prefix → always flag (tel:, tel:abc, tel://+123, etc.)
  if (/^tel:/i.test(base)) return true;

  // Skip known non-phone schemes
  if (/^(https?:|mailto:|javascript:|ftp:|data:|telnet:|ssh:|#|\/)/i.test(base)) return false;

  const digitCount = base.replace(/\D/g, "").length;
  if (digitCount < 7) return false;

  // Any short text followed by non-letter (not a dot+letter domain pattern) and enough digits
  if (/^[a-z]{1,10}[^a-z]/i.test(base) && !/^[a-z]+\.[a-z]/i.test(base)) return true;
  // Bare number (no letters) → +12345678910, 1-800-555-0123
  if (!/[a-z]/i.test(base)) return true;

  return false;
}

export function isValidTelHref(href) {
  if (typeof href !== "string") return false;
  // Reject if contains any whitespace or invisible characters
  if (/[\s\u200B-\u200D\uFEFF]/.test(href)) return false;
  // Must be exactly tel:+ followed by 11 digits (case-insensitive prefix)
  return /^tel:\+\d{11}$/i.test(href);
}

export function normalizeTelHref(href) {
  if (typeof href !== "string") return "";
  // Strip spaces, zero-width spaces, zero-width joiners, BOM
  return href.replace(/[\s\u200B-\u200D\uFEFF]/g, "");
}

export function findInvalidTelHrefs(html) {
  if (!html || typeof html !== "string") return [];

  const doc = parseToHtml(html);
  const invalid = [];

  doc.querySelectorAll("a[href]").forEach(a => {
    const href = a.getAttribute("href") || "";
    if (!looksLikeTelHref(href)) return;
    if (!isValidTelHref(href)) invalid.push(href);
  });

  return invalid;
}
